import { ORIGINAL_MOVEMENT_MANIFEST } from "../data/generated/original-movement-manifest";

export type OriginalMovementDirection = "up" | "down" | "left" | "right";

export type OriginalMovementIntent = {
  dx: number;
  dy: number;
  direction: OriginalMovementDirection;
  targetX: number;
  targetY: number;
  nextX: number;
  nextY: number;
  keyCase?: number;
  evidence: {
    statementId: string;
    procedure: string;
  };
};

export type OriginalWalkStartPlan = OriginalMovementIntent & {
  moveX: number;
  moveY: number;
  axisPriorityStatementId: string;
  passabilityStatementIds: readonly string[];
  eventCheckStatementIds: readonly string[];
  timerStartStatementId: string;
  renderCallStatementIds: readonly string[];
};

export type OriginalWalkingStepFrame = {
  moveCount: number;
  x1: number;
  y1: number;
  mapCacheSourceX: number;
  mapCacheSourceY: number;
  completed: boolean;
  wayAToggleStatementId: string;
  mapDrawMoveStatementId: string;
  completionGateStatementId: string;
};

type StatementRef = {
  statementId: string;
  statementText?: string;
};

type AssignmentRef = StatementRef & {
  expressionText: string;
};

type CallRef = StatementRef & {
  id: string;
  name: string;
  argumentTexts: readonly string[];
};

type KeyTargetRule = {
  keyCase: number;
  direction: OriginalMovementDirection;
  assignment: AssignmentRef;
};

const DECODED_MOVEMENT = ORIGINAL_MOVEMENT_MANIFEST.decodedStateMachine;
const KEY_TARGET_RULES = DECODED_MOVEMENT.inputPolicy.keyToTargetRules as unknown as readonly KeyTargetRule[];
const WALK_START_RULES = DECODED_MOVEMENT.walkStartRules;
const WALKING_SUB_RULES = DECODED_MOVEMENT.walkingSubRules;
const MAP_DRAW_MOVE_CALLS = DECODED_MOVEMENT.renderLinkRules.mapDrawMove.calls as unknown as readonly CallRef[];

const WALK_START_AXIS_PRIORITY = firstStatement("WalkStart axis priority", WALK_START_RULES.axisPriority);
const WALKING_COMPLETION_GATE = firstStatement("WalkingSub completion gate", WALKING_SUB_RULES.completionGate);
const WALKING_MOVE_COUNT_ASSIGNMENT = firstAssignment("WalkingSub MoveCount", WALKING_SUB_RULES.moveCountAssignments);
const MAP_DRAW_MOVE_BITBLT = requireCall("MapDraw_Move BitBlt", MAP_DRAW_MOVE_CALLS, "BitBlt");
const MAP_DRAW_MOVE_WAYA_TOGGLE = requireCall("MapDraw_Move WayA toggle", MAP_DRAW_MOVE_CALLS, "WayA");

export const ORIGINAL_MOVEMENT_RUNTIME_CONSUMPTION = {
  manifest: "docs/original-movement-manifest.json",
  generatedManifestModule: "web/src/data/generated/original-movement-manifest.ts",
  consumesDecodedMovement: true,
  sourceBackedStatus: ORIGINAL_MOVEMENT_MANIFEST.status,
  sourceBackedCriteria: {
    sourceIrMovementProceduresDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.sourceIrMovementProceduresDecoded,
    sourceBackedMovementStatementIds: ORIGINAL_MOVEMENT_MANIFEST.criteria.sourceBackedMovementStatementIds,
    movementStateMachineDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.movementStateMachineDecoded,
    scrollCountersDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.scrollCountersDecoded,
    inputPolicyDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.inputPolicyDecoded,
    keyboardMovementPolicyDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.keyboardMovementPolicyDecoded,
    mouseMovementPolicyDecoded: ORIGINAL_MOVEMENT_MANIFEST.criteria.mouseMovementPolicyDecoded,
    mouseMovementPolicyScopedOutOfMovementGate:
      ORIGINAL_MOVEMENT_MANIFEST.criteria.mouseMovementPolicyScopedOutOfMovementGate,
    frmFullDecodeNotRequiredForMovementGate:
      ORIGINAL_MOVEMENT_MANIFEST.criteria.frmFullDecodeNotRequiredForMovementGate
  },
  consumedStatementIds: {
    keyToTarget: KEY_TARGET_RULES.map((rule) => rule.assignment.statementId),
    walkStartAxisPriority: WALK_START_AXIS_PRIORITY.statementId,
    walkStartDirectionAssignments: Object.fromEntries(
      Object.entries(WALK_START_RULES.directionAssignments).map(([direction, assignments]) => [
        direction,
        (assignments as readonly AssignmentRef[])[0]?.statementId ?? ""
      ])
    ),
    walkStartPassability: statementIds(WALK_START_RULES.passabilityBranches),
    walkStartEventChecks: statementIds(WALK_START_RULES.eventCheckBranches),
    walkStartTimer: firstAssignment("WalkStart timer", WALK_START_RULES.timerStartAssignments).statementId,
    walkStartRenderCalls: statementIds(WALK_START_RULES.renderCalls),
    walkingSubMoveCount: WALKING_MOVE_COUNT_ASSIGNMENT.statementId,
    walkingSubCompletionGate: WALKING_COMPLETION_GATE.statementId,
    walkingSubPositionUpdates: statementIds(WALKING_SUB_RULES.positionUpdates),
    walkingSubWrapRules: statementIds(WALKING_SUB_RULES.wrapRules),
    mapDrawMoveBitBlt: MAP_DRAW_MOVE_BITBLT.statementId,
    mapDrawMoveWayAToggle: MAP_DRAW_MOVE_WAYA_TOGGLE.statementId
  }
} as const;

export function originalMovementRuntimeConsumesDecodedMovement(): boolean {
  return ORIGINAL_MOVEMENT_RUNTIME_CONSUMPTION.consumesDecodedMovement;
}

export function originalKeyboardMovementIntent(
  key: string,
  x: number,
  y: number,
  mapWidth: number,
  mapHeight: number
): OriginalMovementIntent | null {
  const keyCase = originalMovementKeyCase(key);
  if (!keyCase) return null;
  const rule = KEY_TARGET_RULES.find((candidate) => candidate.keyCase === keyCase);
  if (!rule) return null;
  const delta = deltaForDirection(rule.direction);
  return {
    dx: delta.dx,
    dy: delta.dy,
    direction: rule.direction,
    targetX: x + delta.dx,
    targetY: y + delta.dy,
    nextX: wrapCoordinate(x + delta.dx, mapWidth),
    nextY: wrapCoordinate(y + delta.dy, mapHeight),
    keyCase,
    evidence: {
      statementId: rule.assignment.statementId,
      procedure: "KeyCheck"
    }
  };
}

export function originalTryMovePlan(options: {
  x: number;
  y: number;
  dx: number;
  dy: number;
  mapWidth: number;
  mapHeight: number;
}): OriginalWalkStartPlan {
  const targetX = options.x + options.dx;
  const targetY = options.y + options.dy;
  const direction = directionForDelta(options.dx, options.dy);
  const delta = deltaForDirection(direction);
  const directionAssignment = firstDirectionAssignment(direction);
  return {
    dx: delta.dx,
    dy: delta.dy,
    moveX: delta.dx,
    moveY: delta.dy,
    direction,
    targetX,
    targetY,
    nextX: wrapCoordinate(targetX, options.mapWidth),
    nextY: wrapCoordinate(targetY, options.mapHeight),
    axisPriorityStatementId: WALK_START_AXIS_PRIORITY.statementId,
    passabilityStatementIds: statementIds(WALK_START_RULES.passabilityBranches),
    eventCheckStatementIds: statementIds(WALK_START_RULES.eventCheckBranches),
    timerStartStatementId: firstAssignment("WalkStart timer", WALK_START_RULES.timerStartAssignments).statementId,
    renderCallStatementIds: statementIds(WALK_START_RULES.renderCalls),
    evidence: {
      statementId: directionAssignment.statementId,
      procedure: "WalkStart"
    }
  };
}

export function originalWalkingStepFrame(moveCount: number, moveX: number, moveY: number): OriginalWalkingStepFrame {
  const normalizedMoveCount = Math.max(0, Math.trunc(moveCount));
  const x1 = normalizedMoveCount * Math.sign(moveX);
  const y1 = normalizedMoveCount * Math.sign(moveY);
  return {
    moveCount: normalizedMoveCount,
    x1,
    y1,
    mapCacheSourceX: 32 + x1 * 8,
    mapCacheSourceY: 32 + y1 * 8,
    completed: normalizedMoveCount > 3,
    wayAToggleStatementId: MAP_DRAW_MOVE_WAYA_TOGGLE.statementId,
    mapDrawMoveStatementId: MAP_DRAW_MOVE_BITBLT.statementId,
    completionGateStatementId: WALKING_COMPLETION_GATE.statementId
  };
}

function originalMovementKeyCase(key: string): number | null {
  if (key === "ArrowLeft") return 1;
  if (key === "ArrowUp") return 2;
  if (key === "ArrowRight") return 3;
  if (key === "ArrowDown") return 4;
  return null;
}

function directionForDelta(dx: number, dy: number): OriginalMovementDirection {
  if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) return dx < 0 ? "left" : "right";
  if (dy !== 0) return dy < 0 ? "up" : "down";
  return dx < 0 ? "left" : "right";
}

function deltaForDirection(direction: OriginalMovementDirection): { dx: number; dy: number } {
  if (direction === "left") return { dx: -1, dy: 0 };
  if (direction === "right") return { dx: 1, dy: 0 };
  if (direction === "up") return { dx: 0, dy: -1 };
  return { dx: 0, dy: 1 };
}

function wrapCoordinate(value: number, size: number): number {
  if (size <= 0) return value;
  return ((value % size) + size) % size;
}

function firstDirectionAssignment(direction: OriginalMovementDirection): AssignmentRef {
  const assignments = WALK_START_RULES.directionAssignments[direction] as readonly AssignmentRef[];
  return firstAssignment(`WalkStart direction ${direction}`, assignments);
}

function firstStatement(label: string, records: readonly StatementRef[]): StatementRef {
  const record = records[0];
  if (!record?.statementId) throw new Error(`Missing original movement statement: ${label}`);
  return record;
}

function firstAssignment(label: string, records: readonly AssignmentRef[]): AssignmentRef {
  const record = records[0];
  if (!record?.statementId) throw new Error(`Missing original movement assignment: ${label}`);
  return record;
}

function requireCall(label: string, records: readonly CallRef[], name: string): CallRef {
  const record = records.find((candidate) => candidate.name === name);
  if (!record?.statementId) throw new Error(`Missing original movement call: ${label}`);
  return record;
}

function statementIds(records: readonly StatementRef[]): readonly string[] {
  return records.map((record) => record.statementId).filter((statementId) => statementId.length > 0);
}
